import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiUser } from "react-icons/fi";
import ReferralInfo from "../components/dashboard/ReferralInfo";
import VirtualCard from "../components/dashboard/VirtualCard";
import api from "../services/api";

const Profile = () => {
  const navigate = useNavigate();
  const [gender, setGender] = useState("");
  const [idType, setIdType] = useState("");
  const [civilStatusId, setCivilStatusId] = useState("");
  const [nationalityId, setNationalityId] = useState("");
  const [idTypes, setIdTypes] = useState([]);
  const [civilStatuses, setCivilStatuses] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [formData, setFormData] = useState({
    last_name: "",
    first_name: "",
    middle_name: "",
    suffix: "",
    date_of_birth: "",
    home_address: "",
    mobile_number: "",
    email: "",
    guardian_name: "",
    relationship_to_principal: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [idUpload, setIdUpload] = useState(null);
  const [idUploadError, setIdUploadError] = useState("");
  const [isIdLoading, setIsIdLoading] = useState(true);
  const [isRefreshingId, setIsRefreshingId] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      setSubmitErrors([]);
      setSubmitSuccess("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setSubmitErrors(["Missing access token."]);
          return;
        }

        const response = await api.get("/profile", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.status !== "success") {
          setSubmitErrors(["Failed to load profile."]);
          return;
        }

        const data = response.data?.data || {};
        setFormData({
          last_name: data.last_name || "",
          first_name: data.first_name || "",
          middle_name: data.middle_name || "",
          suffix: data.suffix || "",
          date_of_birth: data.date_of_birth || "",
          home_address: data.home_address || "",
          mobile_number: normalizeMobile(data.mobile_number || ""),
          email: data.email || "",
          guardian_name: data.guardian_name || "",
          relationship_to_principal: data.relationship_to_principal || "",
        });
        setGender(data.gender || "");
        setIdType(data.id_type_id ? String(data.id_type_id) : "");
        setCivilStatusId(data.civil_status_id ? String(data.civil_status_id) : "");
        setNationalityId(data.nationality_id ? String(data.nationality_id) : "");
      } catch (error) {
        setSubmitErrors(["Failed to load profile."]);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchLookups = async () => {
      try {
        const [idTypeRes, civilRes, nationalityRes] = await Promise.all([
          api.get("/id-types"),
          api.get("/civil-statuses"),
          api.get("/nationalities"),
        ]);

        if (idTypeRes.data?.status === "success") {
          setIdTypes(idTypeRes.data?.data || []);
        }
        if (civilRes.data?.status === "success") {
          setCivilStatuses(civilRes.data?.data || []);
        }
        if (nationalityRes.data?.status === "success") {
          setNationalities(nationalityRes.data?.data || []);
        }
      } catch (error) {
        return;
      }
    };

    const fetchIdUpload = async () => {
      setIsIdLoading(true);
      setIdUploadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setIdUploadError("Missing access token.");
          return;
        }

        const response = await api.get("/profile/id-upload", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.status !== "success") {
          setIdUploadError("Uploaded ID not found.");
          return;
        }

        setIdUpload(response.data?.data || null);
      } catch (error) {
        setIdUploadError("Failed to load uploaded ID.");
      } finally {
        setIsIdLoading(false);
      }
    };

    fetchProfile();
    fetchIdUpload();
    fetchLookups();
  }, []);

  const normalizeMobile = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (digits.startsWith("63") && digits.length >= 12) {
      return digits.slice(2, 12);
    }
    if (digits.startsWith("0") && digits.length >= 11) {
      return digits.slice(1, 11);
    }
    return digits.slice(0, 10);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setSubmitErrors([]);
    setSubmitSuccess("");

    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setSubmitErrors(["Missing access token."]);
        return;
      }

      if (formData.mobile_number && !/^\d{10}$/.test(formData.mobile_number)) {
        setSubmitErrors([
          "Mobile number must be 10 digits (after +63).",
        ]);
        return;
      }

      const { email, ...payload } = {
        ...formData,
        gender,
        id_type_id: idType || null,
        civil_status_id: civilStatusId || null,
        nationality_id: nationalityId || null,
        mobile_number: formData.mobile_number
          ? `+63${formData.mobile_number}`
          : null,
      };

      const response = await api.put("/profile", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.status !== "success") {
        const message = response.data?.message || "Failed to update profile.";
        if (message && typeof message === "object") {
          const flattened = Object.values(message).flat();
          setSubmitErrors(flattened);
        } else {
          setSubmitErrors([message]);
        }
        return;
      }

      setSubmitSuccess(response.data?.message || "Profile updated.");
    } catch (error) {
      const message = error?.response?.data?.message;
      if (message && typeof message === "object") {
        const flattened = Object.values(message).flat();
        setSubmitErrors(flattened);
      } else if (typeof message === "string") {
        setSubmitErrors([message]);
      } else {
        setSubmitErrors(["Failed to update profile."]);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefreshId = async () => {
    if (!idUpload?.upload_id) {
      return;
    }

    setIsRefreshingId(true);
    setIdUploadError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setIdUploadError("Missing access token.");
        return;
      }

      const response = await api.post(
        "/profile/id-upload/refresh",
        { upload_id: idUpload.upload_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.status !== "success") {
        setIdUploadError("Failed to refresh signed URL.");
        return;
      }

      setIdUpload((prev) => ({
        ...prev,
        signed_url: response.data?.data?.signed_url || prev?.signed_url,
        url_expires_at: response.data?.data?.url_expires_at || prev?.url_expires_at,
      }));
    } catch (error) {
      setIdUploadError("Failed to refresh signed URL.");
    } finally {
      setIsRefreshingId(false);
    }
  };

  const formatBytes = (value) => {
    if (!value && value !== 0) {
      return "-";
    }
    const bytes = Number(value);
    if (Number.isNaN(bytes)) {
      return "-";
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const isUrlExpired = (expiresAt) => {
    if (!expiresAt) {
      return true;
    }
    const expiry = new Date(expiresAt);
    if (Number.isNaN(expiry.getTime())) {
      return true;
    }
    return expiry.getTime() <= Date.now();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mx-auto flex w-full max-w-[1400px] flex-col gap-6"
    >
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          Profile
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-slate-900">
          Personal Information
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Review your details and keep your contact information up to date.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.35fr_0.65fr]">
        <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-[var(--brand-blue)]">
              <FiUser />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                Details
              </p>
              <h2 className="text-2xl font-semibold text-slate-900">
                Personal Information
              </h2>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="mt-6 space-y-5"
          >
            <div className="grid gap-4 lg:grid-cols-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Last Name
                </label>
                <input
                  type="text"
                  placeholder="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  First Name
                </label>
                <input
                  type="text"
                  placeholder="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Middle Name
                </label>
                <input
                  type="text"
                  placeholder="Middle Name"
                  name="middle_name"
                  value={formData.middle_name}
                  onChange={handleChange}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Suffix
                </label>
                <input
                  type="text"
                  placeholder="Suffix"
                  name="suffix"
                  value={formData.suffix}
                  onChange={handleChange}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth || ""}
                  onChange={handleChange}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Gender
                </label>
                <select
                  value={gender}
                  onChange={(event) => setGender(event.target.value)}
                  className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                    gender ? "text-slate-700" : "text-slate-400"
                  }`}
                >
                  <option value="">Gender</option>
                  <option value="male" className="text-slate-700">
                    Male
                  </option>
                  <option value="female" className="text-slate-700">
                    Female
                  </option>
                </select>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Civil Status
                </label>
                <select
                  value={civilStatusId}
                  onChange={(event) => setCivilStatusId(event.target.value)}
                  className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                    civilStatusId ? "text-slate-700" : "text-slate-400"
                  }`}
                >
                  <option value="">Civil Status</option>
                  {civilStatuses.map((status) => (
                    <option key={status.id} value={status.id} className="text-slate-700">
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Nationality
                </label>
                <select
                  value={nationalityId}
                  onChange={(event) => setNationalityId(event.target.value)}
                  className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                    nationalityId ? "text-slate-700" : "text-slate-400"
                  }`}
                >
                  <option value="">Nationality</option>
                  {nationalities.map((nationality) => (
                    <option
                      key={nationality.id}
                      value={nationality.id}
                      className="text-slate-700"
                    >
                      {nationality.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
                    Uploaded ID
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-900">
                    ID Attachment Details
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={handleRefreshId}
                  disabled={
                    !idUpload?.upload_id ||
                    isRefreshingId ||
                    !isUrlExpired(idUpload?.url_expires_at)
                  }
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isRefreshingId ? "Refreshing..." : "Refresh Link"}
                </button>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-700">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">
                    Link Status
                  </p>
                  <p className="mt-1 font-semibold">
                    {isIdLoading
                      ? "Loading..."
                      : isUrlExpired(idUpload?.url_expires_at)
                      ? "Expired"
                      : "Active"}
                  </p>
                </div>
                {!isIdLoading && idUpload?.signed_url && (
                  <a
                    href={idUpload.signed_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                  >
                    View Uploaded ID
                  </a>
                )}
              </div>

              {idUploadError && (
                <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                  {idUploadError}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Home Address
              </label>
              <textarea
                name="home_address"
                value={formData.home_address}
                onChange={handleChange}
                rows={3}
                placeholder="Home Address"
                className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  ID Type
                </label>
                <select
                  value={idType}
                  onChange={(event) => setIdType(event.target.value)}
                  className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                    idType ? "text-slate-700" : "text-slate-400"
                  }`}
                >
                  <option value="">ID Type</option>
                  {idTypes.map((type) => (
                    <option key={type.id} value={type.id} className="text-slate-700">
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Mobile Number
                </label>
                <div className="flex items-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-200">
                  <span className="text-slate-400">+63</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={10}
                    placeholder="Enter 10 digits"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={(event) => {
                      const digits = event.target.value.replace(/\D/g, "");
                      setFormData((prev) => ({
                        ...prev,
                        mobile_number: digits.slice(0, 10),
                      }));
                    }}
                    className="ml-2 w-full border-0 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Email Address
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="email"
                    placeholder="Email Address"
                    name="email"
                    value={formData.email}
                    readOnly
                    className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600"
                  />
                  <button
                    type="button"
                    onClick={() => navigate("/change-email")}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                  >
                    Change Email
                  </button>
                </div>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Name of Guardian
                </label>
                <input
                  type="text"
                  placeholder="Name of Guardian"
                  name="guardian_name"
                  value={formData.guardian_name}
                  onChange={handleChange}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Relationship to Principal
                </label>
                <input
                  type="text"
                  placeholder="Relationship to Principal"
                  name="relationship_to_principal"
                  value={formData.relationship_to_principal}
                  onChange={handleChange}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>

            {submitErrors.length > 0 && (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                <ul className="space-y-1">
                  {submitErrors.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {submitSuccess && (
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                {submitSuccess}
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSaving || isLoading}
                className="rounded-2xl bg-[var(--brand-orange)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                {isSaving ? "Saving..." : "Update Profile"}
              </button>
            </div>
          </form>
        </div>

        <div className="flex flex-col gap-6">
          <ReferralInfo />
          <VirtualCard />
        </div>
      </div>
    </motion.div>
  );
};

export default Profile;
